import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function UploadForm({ events, onUploaded }) {
  const { user } = useAuth();
  const defaultCur = (user?.default_currency || "gbp").toLowerCase();
  const fileRef = useRef();
  const [titlePrefix, setTitlePrefix] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(defaultCur);
  const [eventId, setEventId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({
    done: 0, total: 0,
    batchBytes: 0, batchBytesTotal: 0,
    startedAt: 0,
    errors: [],
  });

  const selectedEvent = events.find((e) => e.id === eventId);
  const groups = selectedEvent?.groups || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!eventId) { toast.error("Choose an event."); return; }
    if (!groupId) { toast.error("Choose a class/group."); return; }
    if (files.length === 0) { toast.error("Add at least one image."); return; }
    if (price === "" || Number(price) < 0) { toast.error("Enter a price of 0 or more (0 = free, admin testing)."); return; }

    const BATCH_SIZE = 6;
    const totalFiles = files.length;
    let totalUploaded = 0;
    const allErrors = [];
    setBusy(true);
    const startedAt = Date.now();
    setProgress({ done: 0, total: totalFiles, batchBytes: 0, batchBytesTotal: 0, startedAt, errors: [] });
    let lastTick = 0;

    try {
      for (let i = 0; i < totalFiles; i += BATCH_SIZE) {
        const batch = files.slice(i, i + BATCH_SIZE);
        const batchBytesTotal = batch.reduce((n, f) => n + f.size, 0);
        const fd = new FormData();
        for (const f of batch) fd.append("files", f);
        fd.append("event_id", eventId);
        fd.append("group_id", groupId);
        fd.append("price", String(price));
        fd.append("currency", currency);
        fd.append("title_prefix", titlePrefix);
        fd.append("description", description);

        setProgress((p) => ({ ...p, batchBytes: 0, batchBytesTotal }));
        lastTick = 0;
        try {
          const { data } = await api.post("/admin/images/bulk", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 5 * 60 * 1000,
            onUploadProgress: (evt) => {
              // Throttle to 4 Hz to keep the render loop from choking on hundreds of files
              const now = Date.now();
              if (now - lastTick < 250 && evt.loaded < (evt.total || batchBytesTotal)) return;
              lastTick = now;
              setProgress((p) => ({ ...p, batchBytes: evt.loaded || 0 }));
            },
          });
          totalUploaded += data.count || 0;
          if (data.errors && data.errors.length) allErrors.push(...data.errors);
        } catch (batchErr) {
          for (const f of batch) {
            allErrors.push({ filename: f.name, error: formatApiError(batchErr) });
          }
        }

        setProgress((p) => ({ ...p, done: totalUploaded, errors: allErrors, batchBytes: batchBytesTotal }));
        // Yield to the browser so it can GC + repaint between batches
        await new Promise((r) => setTimeout(r, 30));
      }

      if (allErrors.length) {
        toast.warning(`${totalUploaded} uploaded · ${allErrors.length} failed`);
      } else {
        toast.success(`Uploaded ${totalUploaded} photo${totalUploaded === 1 ? "" : "s"}`);
      }
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      onUploaded();
    } finally {
      setBusy(false);
    }
  };

  if (events.length === 0) {
    return (
      <div className="border border-white/10 p-10 text-center" data-testid="upload-no-events">
        <p className="text-white/60 mb-2 font-serif text-2xl">Create an event first</p>
        <p className="text-white/40 text-sm">Head to the Events tab and add the date + track before uploading photos.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-10" data-testid="admin-upload-form">
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Event</label>
            <select
              value={eventId}
              onChange={(e) => { setEventId(e.target.value); setGroupId(""); }}
              required
              className="input-minimal bg-black"
              data-testid="upload-event"
            >
              <option value="">— select event —</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.date} · {ev.track}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Class</label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              required
              disabled={!eventId}
              className="input-minimal bg-black disabled:opacity-50"
              data-testid="upload-group"
            >
              <option value="">— select class —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">
            Title prefix (optional)
          </label>
          <input
            value={titlePrefix}
            onChange={(e) => setTitlePrefix(e.target.value)}
            placeholder="e.g. Round 4 — defaults to filename"
            className="input-minimal"
            data-testid="upload-title-prefix"
          />
          <p className="text-xs text-white/30 mt-1">Each photo&apos;s title becomes &ldquo;[prefix] [filename]&rdquo;. Leave blank to use filename only.</p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Description (applied to all)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="input-minimal"
            style={{ resize: "vertical" }}
            data-testid="upload-description"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Price (per photo)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              className="input-minimal"
              placeholder="0 = free (admin test)"
              data-testid="upload-price"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="input-minimal bg-black"
              data-testid="upload-currency"
            >
              <option value="gbp">GBP</option>
              <option value="usd">USD</option>
              <option value="eur">EUR</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="bg-white text-black px-10 py-4 text-xs uppercase tracking-[0.3em] font-medium hover:bg-white/90 transition disabled:opacity-50"
          data-testid="upload-submit"
        >
          {busy
            ? `Uploading ${progress.done}/${progress.total}…`
            : files.length === 0
            ? "Publish photos"
            : files.length === 1
            ? "Publish 1 photo"
            : `Publish ${files.length} photos`}
        </button>

        {busy && progress.total > 0 && (
          <div className="space-y-2" data-testid="upload-progress">
            <div className="h-1 bg-white/10 overflow-hidden">
              <div
                className="h-full bg-white transition-all"
                style={{ width: `${(progress.done / progress.total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-white/60">
              Uploading in batches of 6 · {progress.done}/{progress.total} done
              {progress.errors.length > 0 && <span className="text-red-400 ml-2">· {progress.errors.length} failed so far</span>}
            </p>
          </div>
        )}

        {!busy && progress.total > 0 && (
          <div className="text-xs text-white/60">
            Last batch: {progress.done}/{progress.total} uploaded
            {progress.errors.length > 0 && <span className="text-red-400 ml-2">({progress.errors.length} failed)</span>}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-3">
          Photos ({files.length} selected)
        </label>
        <label
          htmlFor="file-input"
          className="block border border-dashed border-white/20 hover:border-white/50 cursor-pointer min-h-[280px] p-4 bg-[#0a0a0a] transition"
          data-testid="upload-dropzone"
        >
          {files.length === 0 ? (
            <div className="text-center p-10">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">Click to choose photos</p>
              <p className="text-sm text-white/60 mt-3">Select one or many — JPG, PNG, WEBP</p>
              <p className="text-xs text-white/30 mt-2">All photos in this batch share the same event, class, and price.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-[420px] overflow-auto">
              {files.map((f, i) => (
                <div key={`${f.name}-${f.size}-${f.lastModified || i}`} className="relative bg-black/40 aspect-square overflow-hidden">
                  <img
                    src={URL.createObjectURL(f)}
                    alt={f.name}
                    className="w-full h-full object-cover"
                    onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                  />
                  <p className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] text-white/70 px-2 py-1 truncate">{f.name}</p>
                </div>
              ))}
            </div>
          )}
        </label>
        <input
          id="file-input"
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          data-testid="upload-file-input"
        />
        {files.length > 0 && (
          <button
            type="button"
            onClick={() => { setFiles([]); if (fileRef.current) fileRef.current.value = ""; }}
            className="mt-3 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white"
            data-testid="upload-clear"
          >
            Clear selection
          </button>
        )}
      </div>
    </form>
  );
}
